import { QueryClient } from '@tanstack/react-query'

/**
 * Query key patterns that should be invalidated when card data changes.
 * This includes:
 * - Collection entries (card states in tables)
 * - Collection metadata
 * - Virtual collection IDs and counts
 * - Progress indicators
 */
const CARD_CHANGE_QUERY_PREFIXES = [
  'study-deck-items',
  'collection-entries',
  'collection',
  'collection-card-info',
  'collection-entry-ids',
  'virtual-list-card-info'
] as const

/**
 * Invalidate all queries related to card/collection data.
 * Call this when:
 * - New cards are created (JIT creation during study session)
 * - Cards are reviewed (state changes)
 * - Cards are deleted
 *
 * @param queryClient - The React Query client
 * @param userId - The user ID (used for user-specific query keys)
 */
export function invalidateCardQueries(
  queryClient: QueryClient,
  userId: string
): void {
  // Invalidate collection-related queries using predicate matching
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey
      if (!Array.isArray(key)) return false
      const prefix = key[0]
      return (
        typeof prefix === 'string' &&
        CARD_CHANGE_QUERY_PREFIXES.includes(
          prefix as (typeof CARD_CHANGE_QUERY_PREFIXES)[number]
        )
      )
    }
  })

  // Invalidate virtual collection entry IDs (card counts)
  queryClient.invalidateQueries({ queryKey: ['all-card-ids', userId] })
  queryClient.invalidateQueries({ queryKey: ['learning-ids', userId] })
  queryClient.invalidateQueries({ queryKey: ['review-ids', userId] })

  // Invalidate the main collections list (includes virtual collections with counts)
  queryClient.invalidateQueries({ queryKey: ['all-collections', userId] })

  // Invalidate progress indicators
  queryClient.invalidateQueries({
    queryKey: ['curated-collections-progress', userId]
  })
  queryClient.invalidateQueries({
    queryKey: ['user-collections-progress', userId]
  })
}
